/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useCallback, useMemo, useState } from "react";
import { useTenant } from "hooks/settings";
import { backendApi } from "src/store/api/enhancedApi";
import { useAppDispatch } from "src/store/store";
import {
  quantisedWindow,
  useLoadedWindow,
  type Chunk,
} from "components/scrapTracking/helpers";
import type { PerformanceTimeseriesBucket } from "src/store/api/generatedApi";
import { computeBucketSizeSeconds } from "./bucketSizeUtils";
import type { SplitPeriod } from "./filterBucketsBySplit";

const CHUNK_SIZE_MS = 7 * 24 * 60 * 60 * 1000;
const CHUNK_BUFFER = 0;

type UseModelPerformanceDataResult = Readonly<{
  buckets: readonly PerformanceTimeseriesBucket[];
  isLoading: boolean;
  splitPeriods: readonly SplitPeriod[];
}>;

export const useModelPerformanceData = (
  start: Date,
  end: Date,
  targetLocation: string,
  labellingName: string | null,
  areas: readonly string[]
): UseModelPerformanceDataResult => {
  const tenant = useTenant();
  const dispatch = useAppDispatch();
  const [splitPeriods, setSplitPeriods] = useState<readonly SplitPeriod[]>([]);

  const fetchBucketSize = useMemo(
    () => computeBucketSizeSeconds(start.getTime(), end.getTime()),
    [start, end]
  );

  const fetchChunk = useCallback(
    async (chunk: Chunk): Promise<readonly PerformanceTimeseriesBucket[]> => {
      if (!targetLocation || !labellingName) {
        return [];
      }

      const subscription = dispatch(
        backendApi.endpoints.getPerformanceTimeseries.initiate({
          tenantName: tenant,
          start: new Date(chunk.start).toISOString(),
          end: new Date(chunk.end).toISOString(),
          targetLocation: [targetLocation],
          labellingName: [labellingName],
          bucketSizeSeconds: fetchBucketSize,
          ...(areas.length > 0 ? { area: [...areas] } : {}),
        })
      );

      try {
        const result = await subscription.unwrap();
        setSplitPeriods(result?.split_periods ?? []);
        return result?.results?.[0]?.buckets ?? [];
      } finally {
        subscription.unsubscribe();
      }
    },
    [dispatch, tenant, targetLocation, fetchBucketSize, labellingName, areas]
  );

  const quantised = useMemo(
    () =>
      quantisedWindow(
        { start: start.getTime(), end: end.getTime() },
        CHUNK_SIZE_MS,
        CHUNK_BUFFER
      ),
    [start, end]
  );

  const { chunks } = useLoadedWindow(quantised, fetchChunk);

  const mergedBuckets = useMemo(() => {
    const allBuckets: PerformanceTimeseriesBucket[] = [];
    for (const chunk of chunks) {
      if (chunk.value.status === "success") {
        allBuckets.push(...chunk.value.data);
      }
    }
    allBuckets.sort(
      (a, b) =>
        new Date(a.bucket_start).getTime() - new Date(b.bucket_start).getTime()
    );

    const startMs = start.getTime();
    const endMs = end.getTime();
    const seen = new Set<string>();
    return allBuckets.filter((b) => {
      const bucketMs = new Date(b.bucket_start).getTime();
      if (bucketMs < startMs || bucketMs >= endMs) return false;
      const key = `${b.bucket_start}|${b.missing_data_kind ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [chunks, start, end]);

  const isLoading = chunks.some((chunk) => chunk.value.status === "loading");

  return { buckets: mergedBuckets, isLoading, splitPeriods };
};
